import type {
  CurrentTournamentResponse,
  LeaderboardDto,
  SharePrepareRequest,
  SharePrepareResponse,
  ShopDto,
  FriendsDto,
  InviteFriendResponse,
  StarsInvoiceResponse,
  MeDto,
  TelegramAuthRequest,
  TelegramAuthResponse,
  TournamentDto,
  UpdateProfileRequest,
} from '@penalty-cup/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit & { token?: string | null } = {}): Promise<T> {
  const { token, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...rest,
      headers: {
        ...(rest.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  if (res.status === 204) return null as T;
  if (!res.ok) {
    // Код ошибки сервера ({ error: 'invite_expired' }) — чтобы показать понятный текст
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    throw new ApiError(res.status, typeof body?.error === 'string' ? body.error : res.statusText);
  }
  return (await res.json()) as T;
}

export const api = {
  friends: (token: string) => request<FriendsDto>('/friends', { token }),
  inviteFriend: (token: string, tournamentId: string, friendId: number) =>
    request<InviteFriendResponse>('/friends/invite', {
      method: 'POST',
      token,
      body: JSON.stringify({ tournamentId, friendId }),
    }),
  declineInvite: (token: string, code: string) =>
    request<null>('/friends/decline', { method: 'POST', token, body: JSON.stringify({ code }) }),
  shop: (token: string) => request<ShopDto>('/shop', { token }),
  buyItem: (token: string, itemId: string) =>
    request<ShopDto>('/shop/buy', { method: 'POST', token, body: JSON.stringify({ itemId }) }),
  equipItem: (token: string, slot: string, itemId: string) =>
    request<ShopDto>('/shop/equip', { method: 'POST', token, body: JSON.stringify({ slot, itemId }) }),
  starsInvoice: (token: string, itemId: string) =>
    request<StarsInvoiceResponse>('/shop/stars-invoice', {
      method: 'POST',
      token,
      body: JSON.stringify({ itemId }),
    }),
  loginWithTelegram: (initData: string) =>
    request<TelegramAuthResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData } satisfies TelegramAuthRequest),
    }),
  me: (token: string) => request<MeDto>('/me', { token }),
  updateProfile: (token: string, patch: UpdateProfileRequest) =>
    request<MeDto>('/me/profile', { method: 'PATCH', token, body: JSON.stringify(patch) }),
  joinTournament: (token: string) => request<TournamentDto>('/tournaments/join', { method: 'POST', token }),
  createDuel: (token: string) => request<TournamentDto>('/tournaments/duel', { method: 'POST', token }),
  createFriendsCup: (token: string, size: 4 | 8) =>
    request<TournamentDto>('/tournaments/friends', { method: 'POST', token, body: JSON.stringify({ size }) }),
  joinInvite: (token: string, code: string) =>
    request<TournamentDto>(`/tournaments/invite/${encodeURIComponent(code)}`, { method: 'POST', token }),
  startTournament: (token: string, id: string) =>
    request<TournamentDto>(`/tournaments/${id}/start`, { method: 'POST', token }),
  leaveTournament: (token: string, id: string) =>
    request<null>(`/tournaments/${id}/leave`, { method: 'POST', token }),
  prepareShare: (token: string, req: SharePrepareRequest) =>
    request<SharePrepareResponse>('/share/prepare', { method: 'POST', token, body: JSON.stringify(req) }),
  leaderboard: (token: string) => request<LeaderboardDto>('/leaderboard', { token }),
  currentTournament: (token: string) => request<CurrentTournamentResponse>('/tournaments/current', { token }),
};
